import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { auth, db } from '../../firebase';
import { onAuthStateChanged } from 'firebase/auth';
import UserProfile from './UserProfile';
import UserRegistrations from './UserRegistrations';
import TeacherCourses from './TeacherCourses';
import TeacherReimbursements from './TeacherReimbursements';
import TeacherAttendance from './TeacherAttendance';
import { useHeader } from '../../contexts/HeaderContext';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';

interface UserProfile {
    email: string;
    username: string;
    role: 'student' | 'teacher' | 'admin';
    status: 'active' | 'inactive';
    avatar?: string;
}

const ProfilePage: React.FC = () => {
    const [profile, setProfile] = useState<UserProfile | null>(null);
    const [loading, setLoading] = useState(true);
    const [authChecked, setAuthChecked] = useState(false);
    const navigate = useNavigate();
    const { setHeaderTheme } = useHeader();
    const { t } = useTranslation();

    useEffect(() => {
        // 设置全屏渐变背景
        setHeaderTheme({
            bgColor: 'bg-transparent',
            textColor: 'text-white'
        });
        
        return () => {
            setHeaderTheme({
                bgColor: 'bg-white',
                textColor: 'text-[#142b89]'
            });
        };
    }, [setHeaderTheme]);

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (user) => {
            setAuthChecked(true);
            if (!user) {
                setLoading(false);
                navigate('/login');
                return;
            }

            // 检查邮箱验证状态
            if (!user.emailVerified) {
                setLoading(false);
                navigate('/verify-email');
                return;
            }

            try {
                let userDoc = await getDoc(doc(db, 'users', user.uid));
                if (userDoc.exists()) {
                    setProfile(userDoc.data() as UserProfile);
                }else{
                    // 如果用户不存在，则尝试一次创建用户
                    await setDoc(doc(db, 'users', user.uid), {
                        email: user.email,
                        username: user.displayName || user.email,
                        role: 'student',
                        status: 'active',
                        createdAt: new Date().toISOString()
                    });
                    userDoc = await getDoc(doc(db, 'users', user.uid));
                    setProfile(userDoc.data() as UserProfile);
                }
            } catch (error) {
                console.error('获取用户信息失败:', error);
            } finally {
                setLoading(false);
            }
        });

        return () => unsubscribe();
    }, [navigate]);

    // 在认证状态未确认前显示加载状态
    if (!authChecked || loading) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-blue-800 to-blue-600 flex items-center justify-center">
                <motion.div 
                    className="flex flex-col items-center space-y-4"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.5 }}
                >
                    <div className="animate-spin rounded-full h-12 w-12 border-2 border-blue-200 border-t-transparent"></div>
                    <p className="text-white font-medium">{t('profile.loading')}</p>
                </motion.div>
            </div>
        );
    }

    if (!profile) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-blue-800 to-blue-600 flex items-center justify-center">
                <motion.div 
                    className="bg-white/10 backdrop-blur-lg rounded-xl p-8 max-w-md text-center shadow-xl border border-white/20"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                >
                    <p className="text-white mb-6">{t('profile.userNotFound')}</p>
                    <button
                        onClick={() => navigate('/login')}
                        className="px-6 py-2 bg-white text-blue-600 rounded-full font-medium shadow-lg hover:bg-blue-50 transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-blue-600"
                    >
                        {t('profile.backToLogin')}
                    </button>
                </motion.div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-blue-800 to-blue-600 mt-0 pt-16">
            {/* 顶部背景与波浪效果 */}
            <div className="relative w-full overflow-hidden">
                <div className="absolute inset-0 bg-[url('/images/background.jpg')] bg-cover bg-center opacity-10"></div>
                
                {/* 内容层 */}
                <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-20 z-10">
                    <motion.div 
                        className="text-center"
                        initial={{ opacity: 0, y: -20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                    >
                        <h1 className="text-4xl font-bold text-white sm:text-5xl tracking-tight">
                            {t('profile.title')}
                        </h1>
                        <p className="mt-3 max-w-md mx-auto text-xl text-blue-100 sm:text-2xl">
                            {t('profile.subtitle')}
                        </p>
                    </motion.div>
                </div>
            </div>

            {/* 主要内容区域 */}
            <div className="relative pb-20 z-10">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    {/* 用户信息卡片 */}
                    <motion.div 
                        className="backdrop-blur-xl rounded-2xl shadow-2xl overflow-hidden border border-white/20 mb-8"
                        initial={{ opacity: 0, y: 40 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.2 }}
                    >
                        <UserProfile 
                            profile={profile}
                        />  
                    </motion.div>
                    
                    {/* 根据用户角色显示不同内容 */}
                    <motion.div
                        initial={{ opacity: 0, y: 40 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.4 }}
                    >
                        {profile.role === 'student' ? (
                            <UserRegistrations />
                        ) : (
                            <>
                                {(profile.role === 'teacher' || profile.role === 'admin') && (
                                    <>
                                        <TeacherCourses />
                                        <div className="mt-8">
                                            <TeacherReimbursements />
                                        </div>
                                    </>
                                )}
                                {profile.role === 'admin' && (
                                    <div className="my-8">
                                        <TeacherAttendance />
                                    </div>
                                )}
                                <div className={profile.role === 'teacher' || profile.role === 'admin' ? 'mt-8' : ''}>
                                    <UserRegistrations />
                                </div>
                            </>
                        )}
                    </motion.div>
                    
                    {/* 底部装饰元素 */}
                    <div className="mt-12 flex justify-center">
                        <motion.div 
                            className="h-1 w-16 bg-blue-300 rounded-full opacity-50"
                            initial={{ width: 0 }}
                            animate={{ width: 64 }}
                            transition={{ duration: 1, delay: 0.5 }}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ProfilePage; 